import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

export const PRESETS = {
  categorias: [
    { name: 'Excelencia',     minPercentage: 90 },
    { name: 'Honorable',      minPercentage: 79 },
    { name: 'Sobresaliente',  minPercentage: 61 },
    { name: 'Participación',  minPercentage: 0  },
  ],
  letras: [
    { name: 'A', minPercentage: 90 },
    { name: 'B', minPercentage: 79 },
    { name: 'C', minPercentage: 61 },
    { name: 'D', minPercentage: 0  },
  ],
  estrellas: [
    { name: '⭐⭐⭐⭐⭐⭐', minPercentage: 90 },
    { name: '⭐⭐⭐⭐⭐',   minPercentage: 79 },
    { name: '⭐⭐⭐⭐',     minPercentage: 69 },
    { name: '⭐⭐⭐',       minPercentage: 59 },
    { name: '⭐⭐',         minPercentage: 49 },
    { name: '⭐',           minPercentage: 0  },
  ],
};

const PRESET_OPTIONS = [
  { value: 'categorias', label: 'Categorías (Excelencia / Honorable…)' },
  { value: 'letras',     label: 'Letras (A / B / C / D)' },
  { value: 'estrellas',  label: 'Estrellas (⭐ a ⭐⭐⭐⭐⭐⭐)' },
  { value: 'custom',     label: 'Personalizado' },
];

const detectPreset = (categories) => {
  for (const [key, preset] of Object.entries(PRESETS)) {
    if (
      preset.length === categories.length &&
      preset.every((p, i) => p.name === categories[i]?.name && p.minPercentage === categories[i]?.minPercentage)
    ) {
      return key;
    }
  }
  return 'custom';
};

export const RankingCategoriesEditor = ({ value, onChange }) => {
  const [preset, setPreset] = useState(() => detectPreset(value));

  useEffect(() => {
    setPreset(detectPreset(value));
  }, []);

  const handlePresetChange = (e) => {
    const selected = e.target.value;
    if (selected === 'custom') {
      setPreset('custom');
      return;
    }
    setPreset(selected);
    onChange([...PRESETS[selected]]);
  };

  const handleNameChange = (index, name) => {
    const updated = value.map((cat, i) => i === index ? { ...cat, name } : cat);
    onChange(updated);
    setPreset(detectPreset(updated));
  };

  const handlePercentageChange = (index, raw) => {
    const num = Math.min(100, Math.max(0, Number(raw)));
    const updated = value.map((cat, i) => i === index ? { ...cat, minPercentage: isNaN(num) ? 0 : num } : cat);
    onChange(updated);
    setPreset(detectPreset(updated));
  };

  const handleAdd = () => {
    onChange([...value, { name: '', minPercentage: 0 }]);
    setPreset('custom');
  };

  const handleRemove = (index) => {
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
    setPreset(detectPreset(updated));
  };

  const isLastRow = (index) => index === value.length - 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant='subtitle2' color='text.secondary' sx={{ fontWeight: 600, mb: 0.5 }}>
        Clasificación de Ranking
      </Typography>

      <TextField
        select
        label='Preset'
        value={preset}
        onChange={handlePresetChange}
        size='small'
        fullWidth
      >
        {PRESET_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </TextField>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {value.map((cat, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label='Nombre'
              value={cat.name}
              onChange={e => handleNameChange(index, e.target.value)}
              size='small'
              sx={{ flex: 2 }}
            />
            <TextField
              label='Desde %'
              type='number'
              value={isLastRow(index) ? 0 : cat.minPercentage}
              onChange={e => !isLastRow(index) && handlePercentageChange(index, e.target.value)}
              size='small'
              disabled={isLastRow(index)}
              inputProps={{ min: 0, max: 100 }}
              sx={{ flex: 1 }}
            />
            <IconButton
              onClick={() => handleRemove(index)}
              disabled={value.length <= 2}
              size='small'
              color='error'
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Button
        startIcon={<AddIcon />}
        onClick={handleAdd}
        size='small'
        variant='outlined'
        sx={{ alignSelf: 'flex-start' }}
      >
        Agregar categoría
      </Button>
    </Box>
  );
};
